<template>
  <v-dialog
    v-model="isDialogActive"
    max-width="800"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    transition="dialog-bottom-transition"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn :loading="loading" v-bind="attrs" v-on="on" icon @click="openDialog">
        <v-icon>mdi-pencil</v-icon>
      </v-btn>
    </template>
    <v-card v-if="updateModel">
      <v-card-title>
        <span>{{ $t("components.thg.ThgQuoteDetailInformationContractCard.title") }}</span>
        <v-spacer />
        <v-btn icon>
          <v-icon @click="close" color="grey lighten-1">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <debug>{{ updateModel }}</debug>
        <v-form ref="form" v-model="isValid">
          <v-text-field
            :rules="[requiredRule]"
            v-model.number="updateModel.year"
            :label="$t('components.thg.ThgQuoteDetailInformationContractCard.changeYear')"
            class="px-0"
            type="number"
            required
            outlined
            data-test-revenue
          ></v-text-field>

          <v-subheader>{{ $t("report.thgPayoutConfiguration.title") }}</v-subheader>

          <v-row no-gutters>
            <v-col cols="7">
              <v-text-field
                :rules="[minZeroNumberRule]"
                v-model.number="updateModel.payoutConfiguration.revenue"
                :label="$t('components.thg.ThgQuoteDetailInformationContractCard.changePayout')"
                type="number"
                required
                outlined
                data-test-revenue
                append-icon="mdi-currency-eur"
              />
            </v-col>
            <v-col cols="2">
              <div style="display: flex">
                <span class="mt-3 mb-n3 pa-2">
                  {{ $t("components.thg.ThgQuoteDetailInformationContractCard.per") }}
                </span>
                <v-text-field
                  class="mr-2"
                  :rules="[minZeroNumberRule]"
                  :items="units"
                  v-model.number="updateModel.payoutConfiguration.quantity"
                  :label="$t('components.thg.ThgQuoteDetailInformationContractCard.quantity')"
                  type="number"
                  required
                  outlined
                  data-test-revenue
                />
              </div>
            </v-col>
            <v-col cols="3">
              <v-select
                :rules="[requiredRule]"
                :items="units"
                v-model="updateModel.payoutConfiguration.unit"
                :label="$t('components.thg.ThgQuoteDetailInformationContractCard.unit')"
                required
                outlined
                data-test-revenue
              />
            </v-col>
          </v-row>

          <v-checkbox
            class="mt-n4"
            v-model="updateModel.payoutConfiguration.isFixed"
            :label="$t('components.thg.ThgQuoteDetailInformationContractCard.fix')"
            outlined
            dense
            flat
            data-test-fixed
          />

          <div :key="promoUpdateKey">
            <v-subheader>
              {{ $t("common.nouns.promotion") }}
              <v-spacer />
              <v-btn v-if="updateModel.promotionConfiguration" @click="removePromo" x-small text>
                {{ $t("remove") }}
              </v-btn>
              <v-btn v-else @click="addPromo" x-small text>
                {{ $t("add") }}
              </v-btn>
            </v-subheader>
            <v-combobox
              v-if="updateModel.promotionConfiguration"
              v-model="updateModel.promotionConfiguration.type"
              :items="[PromotionTypeEnum.FIRST_PURCHASE]"
              :label="$t('objects.promotion.type')"
              outlined
              dense
              flat
              data-test-fixed
            ></v-combobox>
            <v-text-field
              v-if="updateModel.promotionConfiguration"
              :rules="[minZeroNumberRule]"
              v-model.number="updateModel.promotionConfiguration.value"
              :label="$t('objects.promotion.value')"
              class="px-0"
              type="number"
              :min="0"
              required
              outlined
              data-test-revenue
            ></v-text-field>
          </div>
        </v-form>

        <v-subheader>{{ $t("report.thgChargingStation.meterReading.title") }}</v-subheader>
        <v-form ref="form2" v-model="isValid" v-if="thg.meterReading">
          <v-text-field
            :rules="[requiredRule]"
            v-model="thg.meterReading.startDate"
            :label="$t('components.thg.ThgChargingStationMeterReadingTable.startDate')"
            class="px-0"
            required
            outlined
            data-test-start-date
            disabled
          ></v-text-field>

          <v-text-field
            :rules="[requiredRule]"
            v-model="thg.meterReading.endDate"
            :label="$t('components.thg.ThgChargingStationMeterReadingTable.endDate')"
            class="px-0"
            required
            outlined
            data-test-end-date
            disabled
          ></v-text-field>
          <v-text-field
            v-model.number="thg.meterReading.amountInKwh"
            :rules="[requiredRule]"
            :label="$t('components.thg.ThgChargingStationMeterReadingTable.amountInKwh')"
            outlined
            dense
            flat
            data-test-amount-in-kwh
            disabled
          ></v-text-field>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-btn class="ma-4" color="success" :disabled="!isValid" :loading="loading" @click="save">
          {{ "Save" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { requiredRule } from "@/lib/rules/requiredRule";
import { isNumberWithinRangeRule } from "@/lib/rules/isNumberWithinRangeRule";
import { IThg } from "@/models/thg.entity";
import { Component, Prop, Vue } from "vue-property-decorator";
import Debug from "../utility/Debug.vue";
import { PromotionTypeEnum } from "@/lib/enum/promotion-type.enum";
import { IUpdateThg, UpdateThg } from "@/models/update-thg.entity.ts";
import { deepCopy } from "@/lib/utility/deep-copy";
import { UnitEnum } from "@/lib/enum/unit.enum";

@Component({
  components: { Debug }
})
export default class ThgAdminUpdatePayoutConfigurationDialog extends Vue {
  @Prop()
  thg!: IThg;

  /**
   * Displays loading animation
   */
  @Prop({ default: false })
  loading!: boolean;

  readonly PromotionTypeEnum = PromotionTypeEnum;

  /**
   * Force refresh to fix reactivity after promotion object was added/removed from THG
   */
  promoUpdateKey = 0;

  /**
   * Form valid
   */
  isValid = false;

  /**
   * Show Dialog
   */
  isDialogActive = false;

  updateModel: IUpdateThg | null = null;

  openDialog() {
    this.isDialogActive = true;
    this.updateModel = new UpdateThg(deepCopy(this.thg));
  }

  get units() {
    return Object.values(UnitEnum);
  }

  get requiredRule() {
    return requiredRule();
  }

  get minZeroNumberRule() {
    return isNumberWithinRangeRule(0);
  }
  removePromo() {
    if (!this.updateModel?.promotionConfiguration) return;
    this.updateModel.promotionConfiguration = null;
    this.promoUpdateKey++;
  }

  addPromo() {
    if (!this.updateModel) return;
    this.updateModel.promotionConfiguration = { type: PromotionTypeEnum.FIRST_PURCHASE, value: 0 };
    this.promoUpdateKey++;
  }

  async save(): Promise<IThg> {
    this.isDialogActive = false;

    this.$emit("save", this.updateModel);
    this.close();

    return this.thg;
  }

  async close() {
    this.isDialogActive = false;
    this.$emit("refresh");
  }
}
</script>
